import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  activePlansHasFreeTrial,
  formatUpcomingDate,
  getLiveMetaData,
  getMovieMetadata,
  getOriginalAirdateMetaData,
  getShowType,
  HTML_ENTITY_BULLET,
  isAudio,
  isChannelType,
  isClip,
  isContinueListening,
  isContinueWatching as isContinueWatchingHelper,
  isEpgLive,
  isEpisode,
  isLive,
  isMovie,
  isSeries,
  isShowcaseCountdown,
  isSpecial,
  isTypeCategory,
  isTypeEPGListing,
  isTypePromo,
  isTypeScreenPanel,
  isTypeSeries,
  isTypeVideo,
  isUpcoming,
  plurarize,
} from 'helpers/ContentHelper';
import { FAVORITE_PLUS } from 'components/Favorite';
import { getPromoData, PROMO_TYPE_LEAD } from 'helpers/SeriesDetailHelpers';
import { now, stringToDate, isAfter, differenceInSeconds } from 'helpers/DateTimeHelper';
import {
  setSegmentRestartedPlaybackGlobal,
  setSegmentResumedPlaybackGlobal,
  PageNames,
  SegmentSources,
  tealiumEventPageTypes,
} from '@foxcorp/delta-web-analytics';

import {
  formatAiredDate,
  formatDuration,
  formatDurationInSeconds,
  formatRating,
  formatSeasonEpisodeExtended,
  formatSubRatings,
} from 'helpers/VideoHelper';
import {
  SHOWCASE_TYPES as showcase,
  BADGE_TYPES as badge,
  BUTTON_TYPES as button,
} from 'helpers/TypeHelper';
import {
  helperGetLiveUrl,
  helperGetRelativeUrl,
  helperGetStaticContentUrl,
  helperGetVideoUrl,
  redirectToExternalUrl,
  helperGetUpsellUrl,
} from 'helpers/RouteHelper';
import { COLLECTION_HERO_LOGO_IMAGE, PERSONALITY_HERO_AVATAR_IMAGE } from 'helpers/ImageHelper';
import { RESTART_TYPE_HOME } from 'screens/Watch/WatchHelpers';
import { ANON_SERIES, CHANNEL, COLLECTION, AUTH_SERIES, EVENT } from '../index';

const {
  global_moreClips: moreClipsText,
  global_moreInfoButton: moreInfoText,
  global_moreSeriesDeeplinkButton: moreEpisodesText,
  global_playNowLabel: playNowText,
  global_restartButton: restartText,
  global_seeAllSeriesButton: seeAllEpisodesText,
  global_seeAllCategoryButton: seeAllCategoryText = 'See More',
  global_startWatchingButton: startWatchingButtonText,
  global_watchLiveButton: watchLiveText,
  showcase_watchButton: showcaseWatchNowText,
  seriesDetail_retiredShowTuneInLabel: retiredTuneInLabel,
  upsellPromoNavCTA,
  upsellPromoNavFreemiumCTA,
} = window.Language;

const getMobileInfoProps = ({ item, buttons }) => {
  const seriesType = item?.seriesType;
  const videoType = item?.videoType;
  let showTitle = false;
  let showDescription = false;

  if (isSeries(seriesType) && isEpisode(videoType)) {
    showTitle = true;
  } else if ((isSpecial(seriesType) || isMovie(videoType)) && isEpisode(videoType)) {
    showDescription = true;
  } else if (isTypeEPGListing(seriesType) && isSeries(videoType)) {
    showTitle = true;
  } else if (isSeries(seriesType) && isClip(videoType)) {
    showDescription = true;
  } else if ((isSpecial(seriesType) || isMovie(seriesType)) && isMovie(videoType)) {
    showDescription = true;
  } else if (isTypeCategory(seriesType) && isSeries(videoType)) {
    showDescription = true;
  }

  const playItems = buttons.filter(button => {
    const buttonItem = button?.label?.toLowerCase();
    return buttonItem === 'play' || buttonItem === 'resume';
  })?.[0];
  let url = playItems?.url;
  if (item?.isLiveNow) url = `/live/${item?.id}`;
  const isPlayable = !!playItems || item?.isLiveNow;
  const timeLeft = Math.floor((item?.durationInSeconds - item?.bookmarkSeconds) / 60);
  return {
    isPlayable,
    showDescription,
    showTitle,
    timeLeft,
    url,
  };
};

const getShowcaseType = item => {
  const { secUntilStart, seriesType, '@type': type, videoType } = item;
  if (isShowcaseCountdown(item)) return showcase.SHOWCASE_COUNTDOWN;
  if (isLive(type)) {
    const startsInFuture = secUntilStart && secUntilStart > 0;
    if (startsInFuture) return showcase.SHOWCASE_UPCOMING;
    return showcase.SHOWCASE_LIVE;
  }
  if (isTypeSeries(type)) return showcase.SHOWCASE_SERIES;
  if (isTypePromo(type)) return showcase.SHOWCASE_PROMO;
  if (isTypeCategory(type)) return showcase.SHOWCASE_CATEGORY;
  if (isTypeVideo(type)) {
    if (isClip(videoType)) return showcase.SHOWCASE_CLIP;
    if (isMovie(videoType)) return showcase.SHOWCASE_MOVIE;
    if (isEpisode(videoType)) {
      if (isSpecial(seriesType)) return showcase.SHOWCASE_SPECIAL;
      return showcase.SHOWCASE_SERIES_EPISODE;
    }
  }
  return null;
};

const getItemData = item => {
  const { '@type': type, videos } = item;
  if (isTypePromo(type)) {
    const description = item?.description ?? videos?.[0]?.description ?? '';
    return {
      ...item,
      ...(videos?.[0] ?? {}),
      autoPlayStill: videos?.[0]?.autoPlayStill ?? null,
      autoPlayVideo: videos?.[0]?.autoPlayVideo ?? null,
      description,
      headline: item?.headline ?? videos?.[0]?.headline ?? '',
      images: videos?.[0]?.images ?? null,
      isCustomDescription: !!(item?.description ?? null),
      isPromoContent: true,
      promoBackgroundImage: item?.images?.promoImage ?? null,
      promoBackgroundVideo: videos?.autoPlayVideo?.default?.url ?? null,
      promoPlayerScreenUrl: videos?.[0]?.playerScreenUrl ?? null,
      promoSeriesScreenUrl: videos?.[0]?.seriesScreenUrl ?? null,
      showcaseSquareBackgroundImage: item?.images?.showcaseSquare ?? null,
    };
  }
  if (isTypeScreenPanel(type)) {
    return item?.items?.member?.[0] ?? item;
  }
  return item;
};

const getOnClick = ({
  analytics,
  buttonTypes,
  primaryText,
  secondaryText,
  tertiaryText,
  callCollectionImpressionEvent,
  additionalFunctions,
}) => {
  const {
    isRestartBtn,
    primaryButtonIsPlayButton,
    isResumeBtn,
    isRestartSecondaryBtn,
  } = buttonTypes;
  const primaryOnClick = () => {
    analytics.handleSegmentItemClick(primaryText);
    callCollectionImpressionEvent();
    if (primaryButtonIsPlayButton) {
      analytics.logPlayer();
    }
    if (analytics.isLive) analytics.handleLiveClick(additionalFunctions);
    analytics.onSelection({ itemIndex: null, additionalFunctions });
    analytics.onHandleTealiumButtonAction({
      text: primaryText,
    });
    if (isResumeBtn) {
      setSegmentResumedPlaybackGlobal(true);
    }
    if (isRestartBtn) {
      additionalFunctions?.saveLiveRestart(analytics.item);
      setSegmentRestartedPlaybackGlobal(true);
    }
    analytics.tealiumCollectionEvents(primaryText);
  };

  const secondaryOnClick = () => {
    analytics.handleSegmentItemClick(secondaryText);
    callCollectionImpressionEvent();
    if (analytics.isLive) analytics.handleLiveClick();
    analytics.onSelection({ itemIndex: null, additionalFunctions });
    analytics.onHandleTealiumButtonAction({
      text: secondaryText,
    });
    if (isRestartSecondaryBtn) {
      additionalFunctions?.saveLiveRestart(analytics.item);
      setSegmentRestartedPlaybackGlobal(true);
    }
    analytics.tealiumCollectionEvents(secondaryText);
  };

  const tertiaryOnClick = () => {
    analytics.handleSegmentItemClick(tertiaryText);
    analytics.onSelection({ itemIndex: null, additionalFunctions });
    analytics.onHandleTealiumButtonAction({ text: tertiaryText });
  };

  return { primaryOnClick, secondaryOnClick, tertiaryOnClick };
};
const getBadgeType = item => {
  if (isContinueListening(item)) return badge.BADGE_CONTINUE_LISTENING;
  if (isAudio(item)) return badge.BADGE_AUDIO;
  if (isEpgLive(item)) return badge.BADGE_LIVE;
  if (isUpcoming(item)) return badge.BADGE_UPCOMING;
  if (isContinueWatchingHelper(item)) return badge.BADGE_CONTINUE_WATCHING;
  return null;
};

const getBackgroundProps = ({ item, type, buttons }) => {
  let sizzle;
  let isSeries = false;
  let url;
  let onClick;
  const image = {};
  const alt = item?.series?.id;
  const videoId = `${item?.id}-${uuidv4()}-sizzle`;
  const primaryOnClick = buttons?.[0]?.onClick;
  const primaryUrl = buttons?.[0]?.url;
  const secondaryOnClick = buttons?.[1]?.onClick;
  const secondaryUrl = buttons?.[1]?.url;
  const seriesSquareImg = item?.images?.seriesSquare?.raw;
  const seriesDetailImg = item?.images?.seriesDetail?.raw;
  const seriesDetailMobileImg = item?.images?.still?.raw;
  const showcaseSquareImg =
    item?.showcaseSquareBackgroundImage || item?.images?.showcaseSquare?.raw;
  const isShowcaseSquareImgFallback = showcaseSquareImg?.includes('fallback');
  const isSeriesSquareImgFallback = seriesSquareImg?.includes('fallback');
  let unoptimized = false;
  const showCaseType = getShowcaseType(item);
  const isPromoType = showCaseType === showcase.SHOWCASE_PROMO || !!item?.isPromoContent;
  const fallbackImage = helperGetStaticContentUrl('images', 'FallBack-FOXNATION-min.jpg');
  switch (type) {
    case CHANNEL:
      sizzle = item?.promoBackgroundVideo || item?.autoPlayVideo?.default?.url;
      image.desktop = (isPromoType && item?.promoBackgroundImage) || seriesDetailImg;
      image.mobile =
        (!isShowcaseSquareImgFallback && showcaseSquareImg) ||
        (!isSeriesSquareImgFallback && seriesSquareImg) ||
        seriesDetailMobileImg ||
        seriesDetailImg;
      url = primaryUrl || secondaryUrl;
      onClick = primaryOnClick || secondaryOnClick;
      break;
    case ANON_SERIES:
    case AUTH_SERIES:
      image.desktop = seriesDetailImg;
      image.mobile = seriesDetailMobileImg;
      isSeries = true;
      break;
    case COLLECTION:
      sizzle = item?.autoPlayVideo?.default?.url;
      image.desktop =
        (isPromoType && item?.promoBackgroundImage) || seriesDetailImg || item?.images?.still?.raw;
      image.mobile =
        (!isShowcaseSquareImgFallback && showcaseSquareImg) ||
        (!isSeriesSquareImgFallback && seriesSquareImg) ||
        seriesDetailMobileImg ||
        seriesDetailImg;
      url = primaryUrl || secondaryUrl;
      onClick = primaryOnClick || secondaryOnClick;
      break;
    case EVENT:
      image.desktop =
        item?.eventData?.keyArt?.seriesDetail || item?.promoBackgroundImage || seriesDetailImg;
      image.mobile =
        item?.eventData?.keyArt?.seriesSquare ||
        (!isShowcaseSquareImgFallback && showcaseSquareImg) ||
        image.desktop;
      unoptimized = true;
      url = secondaryUrl || primaryUrl;
      onClick = secondaryOnClick || primaryOnClick;
      break;
    default:
      break;
  }

  if (!image.desktop) image.desktop = fallbackImage;
  if (!image.mobile) image.mobile = fallbackImage;

  return { alt, image, isSeries, sizzle, onClick, url, unoptimized, videoId };
};

const getBookmarkItem = ({ bookmarks, uID }) => {
  const bookmarkList = bookmarks?.list;
  return bookmarkList?.filter(bookmark => bookmark.uID === uID)?.[0];
};

const getProgressBar = ({ item, bookmarks }) => {
  const bookmarkItem = getBookmarkItem({ bookmarks, uID: item?.uID });
  return {
    isContinueWatching: isContinueWatchingHelper(item),
    startDate: item?.startDate,
    endDate: item?.endDate,
    percentWatched: bookmarkItem?.percentWatched,
  };
};

const getSeriesMetadata = ({ item, episode }) => {
  const {
    contentRating,
    fullEpisodeCount,
    genres = [],
    seasonCount,
    seriesType,
    currentSeason = {},
    subRatings,
  } = item;
  const { originalAirDate, dateCreated, type, durationInSeconds } = episode ?? '';
  const rating = formatRating(contentRating);
  const originalDate = isClip(type) ? dateCreated : originalAirDate;
  const airDate = formatAiredDate(originalDate);
  const bullet = originalDate ? HTML_ENTITY_BULLET : null;
  let seasonInfo = '';
  let ratingInfo = '';
  let airDateInfo = '';
  if (seasonCount > 0) {
    const { seasonNumber } = currentSeason;

    const {
      seriesDetail_seasonLabel: seasonLabel,
      seriesDetail_seasonsLabel: seasonsLabel,
      global_episodeLabel: foxEpisodeLabel,
      global_episodesLabel: foxEpisodesLabel,
    } = window.Language;

    const seasonText = plurarize({
      count: seasonNumber,
      singular: seasonLabel,
      plural: seasonsLabel,
      reverse: true,
    });
    const episodeText = plurarize({
      count: fullEpisodeCount,
      singular: foxEpisodeLabel,
      plural: foxEpisodesLabel,
    });
    seasonInfo = `${seasonText} (${episodeText}) ${HTML_ENTITY_BULLET} `;
    ratingInfo = rating ? `${HTML_ENTITY_BULLET} ${rating}}` : '';
  } else if (!_.isEmpty(episode)) {
    airDateInfo = airDate ? `${airDate} ${rating ? `${bullet} ${rating}}` : ''}` : '';
  }
  if (seriesType === 'movie' || seriesType === 'special') {
    airDateInfo = '';
    seasonInfo = durationInSeconds ? `${formatDurationInSeconds(durationInSeconds)}` : '';
    ratingInfo = rating ? `${rating} ${formatSubRatings(subRatings)}` : '';
  }

  return { seasonInfo, ratingInfo, airDateInfo, genres };
};

const getMetadata = (item = {}, showcaseType) => {
  const {
    currentSeason,
    durationInSeconds,
    fullEpisodeCount,
    originalAirDate,
    releaseYear,
    seasonCount,
    startDate,
  } = item;

  let metaData = formatUpcomingDate(startDate);
  switch (showcaseType) {
    case showcase.SHOWCASE_UPCOMING:
    case showcase.SHOWCASE_COUNTDOWN:
    case showcase.SHOWCASE_CATEGORY: {
      metaData = formatUpcomingDate(startDate);
      break;
    }
    case showcase.SHOWCASE_LIVE: {
      metaData = getLiveMetaData({ startDate, durationInSeconds });
      break;
    }
    case showcase.SHOWCASE_SERIES:
    case showcase.SHOWCASE_PROMO: {
      metaData = formatSeasonEpisodeExtended(seasonCount, currentSeason, fullEpisodeCount);
      break;
    }

    case showcase.SHOWCASE_SPECIAL:
    case showcase.SHOWCASE_SERIES_EPISODE: {
      metaData = getOriginalAirdateMetaData({
        originalAirDate,
        durationInSeconds,
      });
      break;
    }
    case showcase.SHOWCASE_MOVIE: {
      metaData = getMovieMetadata({
        releaseYear,
        durationInSeconds,
      });
      break;
    }
    case showcase.SHOWCASE_CLIP: {
      metaData = getMovieMetadata({ durationInSeconds });
      break;
    }
    default:
  }
  return metaData;
};

const getAirDate = episode => {
  const { type, dateCreated, originalAirDate, duration, durationInSeconds } = episode || [];
  const originalDate = isClip(type) ? dateCreated : originalAirDate;
  const airDate = formatAiredDate(originalDate);
  const moreThanAMinute = durationInSeconds > 60;
  const formattedDuration = duration && moreThanAMinute ? formatDuration(duration) : null;
  const bullet = originalDate && formattedDuration ? HTML_ENTITY_BULLET : null;
  if (!originalDate) return null;
  return {
    airDate,
    bullet,
    formattedDuration,
  };
};
const getDescription = item => item?.description;
const getEventDescription = item => item?.eventData?.description;
const getTitle = item => item?.headline;
const getSeriesTuneIn = (item, episode) => {
  const { retired, tuneIn } = item;
  const { originalAirDate } = episode;
  const airDate = formatAiredDate(originalAirDate);
  let tuneInText = retired ? tuneIn || retiredTuneInLabel : tuneIn;
  if (!tuneInText) tuneInText = airDate.replace(/-/g, '/');
  return tuneInText;
};
const getLogo = item => {
  const title = item?.name;
  const alt = item?.images?.logo?.altText;
  const url = item?.images?.logo?.raw;
  const centeredImg = item?.images?.logoCenter?.raw;
  return {
    alt,
    context: COLLECTION_HERO_LOGO_IMAGE,
    img: url,
    centeredImg,
    title,
  };
};
const getEventLogo = item => {
  const title = item?.seriesName;
  const alt = item?.images?.logo?.altText;
  const url = item?.eventData?.keyArt?.logo || item?.images?.logo?.raw;
  return {
    alt,
    context: COLLECTION_HERO_LOGO_IMAGE,
    img: url,
    title,
  };
};

const getAvatarLogo = item => {
  const fallbackImage = helperGetStaticContentUrl('images', 'FallBack-FOXNATION-min.jpg');
  const title = item?.name;
  const alt = item?.name;
  const url = item?.images?.still?.raw ?? fallbackImage;
  return {
    alt,
    context: PERSONALITY_HERO_AVATAR_IMAGE,
    img: url,
    title,
  };
};
const getAlternativeHeadline = item => item?.alternativeHeadline;
const getBadge = item => {
  const text = getBadgeType(item);
  const type = text && text?.toLowerCase()?.replace(/ /g, '-');
  return { text, type };
};

const isLatest = showcaseOrder => showcaseOrder === 'latest' || showcaseOrder === 'personalized';
const getEventStartTime = item => item?.eventData?.startTime;

const getEpisode = ({ item, continueWatchingItems }) => {
  const { showcaseOrder, oldestEpisode = {}, latestEpisode, latestMovie } = item || {};
  let episode = {};
  const watchItem = continueWatchingItems?.[0];
  if (watchItem && !watchItem?.watched) {
    [episode] = continueWatchingItems;
  } else if (isLatest(showcaseOrder)) {
    episode = latestEpisode ?? latestMovie ?? {};
  } else {
    episode = oldestEpisode;
  }
  return episode;
};

const getButtons = ({
  item: startingItem,
  callCollectionImpressionEvent,
  analytics,
  additionalFunctions,
}) => {
  const {
    callSign,
    '@id': url,
    '@type': type,
    id,
    categoryType: typeCategory,
    isSeriesDetailAvailable,
    percentWatched,
    playerScreenUrl,
    seriesSeasonEpisode,
    startDate,
    endDate,
    isPromoContent,
    seriesScreenUrl,
    seriesPlayerScreenUrl,
    promoPlayerScreenUrl,
    promoSeriesScreenUrl,
    livePlayerScreenUrl,
  } = startingItem || {};
  let secUntilStart = null;
  const currentDate = now();
  const startsAt = stringToDate(startDate);
  const endsAt = stringToDate(endDate);
  let liveVideoEnded = false;
  const isUpcoming = isAfter(startsAt, currentDate);
  if (isLive(type)) {
    if (isUpcoming) {
      secUntilStart = differenceInSeconds(startsAt, currentDate);
    }
    if (isAfter(currentDate, endsAt)) liveVideoEnded = true;
  }
  const videoHasLiveUrl = !!livePlayerScreenUrl;
  const item = { ...startingItem, secUntilStart };
  const watchLink = helperGetRelativeUrl({
    url: isPromoContent
      ? promoPlayerScreenUrl || promoSeriesScreenUrl
      : playerScreenUrl || seriesScreenUrl || seriesPlayerScreenUrl,
  });
  let seriesUrl = seriesScreenUrl;
  const isCategory = isTypeCategory(type);
  const isChannelItem = isCategory && isChannelType(typeCategory);
  if (isChannelItem) {
    seriesUrl = url.replace('categories', 'channel');
  }
  const titleLink = helperGetRelativeUrl({
    url: isPromoContent ? promoSeriesScreenUrl : seriesUrl,
  });
  const detailPageAvailable = titleLink && isSeriesDetailAvailable !== false;
  let isResumeBtn = percentWatched && percentWatched > 0 && percentWatched < 100;
  let primaryButtonText;
  let primaryButtonType;
  let primaryButtonLink;
  let secondaryButtonLink;
  let secondaryButtonText;
  let secondaryButtonType;
  let tertiaryButtonLink;
  let tertiaryButtonText;
  let tertiaryButtonType;
  let isRestartBtn;
  let primaryButtonIsPlayButton = false;
  let isRestartSecondaryBtn = false;
  const restartEnabled = item?.originalVideo?.restartEnabled;
  const isRestartEnabled = typeof restartEnabled !== 'undefined' ? restartEnabled : true;
  const displaySeasonAndEpisodeCounts = _.get(item, 'displaySeasonAndEpisodeCounts', true);
  const showcaseType = getShowcaseType(item);
  switch (showcaseType) {
    case showcase.SHOWCASE_LIVE: {
      if (videoHasLiveUrl && !liveVideoEnded) {
        primaryButtonText = watchLiveText;
        primaryButtonLink = isUpcoming ? null : helperGetLiveUrl({ callSign, videoId: id });
        primaryButtonType = button.WATCH;
        primaryButtonIsPlayButton = true;
        if (playerScreenUrl && isRestartEnabled) {
          secondaryButtonText = restartText;
          const restartLink = helperGetRelativeUrl({ url: playerScreenUrl });
          secondaryButtonLink = `${restartLink}#${RESTART_TYPE_HOME}`;
          secondaryButtonType = button.RESTART;
          isRestartSecondaryBtn = true;
          if (detailPageAvailable) {
            tertiaryButtonText = moreInfoText;
            tertiaryButtonLink = titleLink;
            tertiaryButtonType = button.MORE_DETAILS;
          }
        } else if (detailPageAvailable) {
          secondaryButtonText = moreInfoText;
          secondaryButtonLink = titleLink;
          secondaryButtonType = button.MORE_DETAILS;
        }
      } else if (detailPageAvailable) {
        primaryButtonText = moreInfoText;
        primaryButtonLink = titleLink;
        primaryButtonType = button.MORE_DETAILS;
      }
      isRestartBtn = false;
      isResumeBtn = false;
      break;
    }
    case showcase.SHOWCASE_UPCOMING: {
      const videoStartsWithinFiveMinutes = secUntilStart && secUntilStart <= 300;
      const canPlay = videoHasLiveUrl && videoStartsWithinFiveMinutes;
      if (canPlay) {
        primaryButtonText = watchLiveText;
        primaryButtonType = button.WATCH;
        primaryButtonLink = helperGetLiveUrl({ callSign, videoId: id });
        primaryButtonIsPlayButton = true;
        if (detailPageAvailable) {
          secondaryButtonText = moreInfoText;
          secondaryButtonLink = titleLink;
          secondaryButtonType = button.MORE_DETAILS;
        }
      } else if (detailPageAvailable) {
        primaryButtonText = moreInfoText;
        primaryButtonLink = titleLink;
        primaryButtonType = button.MORE_DETAILS;
      }
      break;
    }
    case showcase.SHOWCASE_SERIES: {
      if (watchLink) {
        primaryButtonText = displaySeasonAndEpisodeCounts
          ? `${startWatchingButtonText} ${seriesSeasonEpisode}`
          : showcaseWatchNowText;
        primaryButtonType = button.WATCH;
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = seeAllEpisodesText;
        secondaryButtonLink = titleLink;
        secondaryButtonType = button.MORE_DETAILS;
      }

      break;
    }
    case showcase.SHOWCASE_SERIES_EPISODE: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonType = button.WATCH;
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreEpisodesText;
        secondaryButtonLink = titleLink;
        secondaryButtonType = button.MORE_DETAILS;
      }
      break;
    }
    case showcase.SHOWCASE_SPECIAL: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonType = button.WATCH;
        primaryButtonLink = watchLink;
      }
      if (titleLink) {
        secondaryButtonLink = titleLink;
        secondaryButtonText = moreInfoText;
        secondaryButtonType = button.MORE_DETAILS;
      }
      break;
    }
    case showcase.SHOWCASE_PROMO: {
      if (watchLink) {
        primaryButtonText = playNowText;
        primaryButtonType = button.WATCH;
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreEpisodesText;
        secondaryButtonLink = titleLink;
        secondaryButtonType = button.MORE_DETAILS;
      }
      break;
    }
    case showcase.SHOWCASE_MOVIE: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonType = button.WATCH;
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreInfoText;
        secondaryButtonLink = titleLink;
        secondaryButtonType = button.MORE_DETAILS;
      }
      break;
    }
    case showcase.SHOWCASE_CLIP: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonType = button.WATCH;
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreClipsText;
        secondaryButtonLink = titleLink;
        secondaryButtonType = button.MORE_DETAILS;
      }
      break;
    }
    case showcase.SHOWCASE_CATEGORY: {
      if (titleLink) {
        secondaryButtonLink = titleLink;
        secondaryButtonText = seeAllCategoryText;
        secondaryButtonType = button.MORE_DETAILS;
      }
      break;
    }
    case showcase.SHOWCASE_COUNTDOWN: {
      if (detailPageAvailable) {
        secondaryButtonText = moreInfoText;
        secondaryButtonLink = titleLink;
        secondaryButtonType = button.MORE_DETAILS;
      }
      isRestartBtn = false;
      isResumeBtn = false;

      break;
    }
    default:
  }
  const buttonTypes = {
    isRestartBtn,
    primaryButtonIsPlayButton,
    isResumeBtn,
    isRestartSecondaryBtn,
  };
  const { primaryOnClick, secondaryOnClick, tertiaryOnClick } = getOnClick({
    analytics,
    buttonTypes,
    primaryText: primaryButtonText,
    secondaryText: secondaryButtonText,
    tertiaryText: tertiaryButtonText,
    callCollectionImpressionEvent,
    additionalFunctions,
  });
  const buttons = [];
  const primary = {
    type: primaryButtonType,
    url: primaryButtonLink,
    label: primaryButtonText,
    primary: true,
    onClick: primaryOnClick,
  };

  const secondary = {
    type: secondaryButtonType,
    url: secondaryButtonLink,
    label: secondaryButtonText,
    primary: false,
    onClick: secondaryOnClick,
  };

  const tertiary = {
    type: tertiaryButtonType,
    url: tertiaryButtonLink,
    label: tertiaryButtonText,
    primary: false,
    onClick: tertiaryOnClick,
  };
  if (primaryButtonLink) buttons.push(primary);
  if (secondaryButtonLink) buttons.push(secondary);
  if (tertiaryButtonLink) buttons.push(tertiary);
  return buttons;
};

const getPlayButtonUrl = (item = {}, latestFullEpisodeId = '') => {
  const { type, playerScreenUrl, videoId } = item;
  let link = '';
  if (isTypeVideo(type)) {
    link = playerScreenUrl
      ? helperGetRelativeUrl({ url: playerScreenUrl })
      : helperGetVideoUrl(videoId);
  } else if (latestFullEpisodeId) {
    link = `/watch/${latestFullEpisodeId}`;
  } else {
    link = helperGetRelativeUrl({ url: playerScreenUrl });
  }
  return link;
};

const getPlayProps = ({ episode = {}, item, isAnonymous, analytics }) => {
  const {
    seriesType,
    seasonNumber,
    episodeNumber,
    percentWatched,
    displaySeasonAndEpisodeCounts,
    watched,
  } = episode;
  const { orderSeasons, latestFullEpisodeId } = episode;
  const url = isAnonymous
    ? getPlayButtonUrl(item, item?.latestFullEpisodeId ?? item?.latestMovieId ?? null)
    : getPlayButtonUrl(episode, latestFullEpisodeId);
  const shouldRender = !!url;
  const isLatest = !orderSeasons || orderSeasons === 'desc';
  const isUngatedVideo = item?._availabilityTags?.map(text => text.toLowerCase())?.includes('free');
  let text = isUngatedVideo && isAnonymous ? 'Play Free Episode' : 'Play';
  const seasonAndEpisodeNumberText =
    displaySeasonAndEpisodeCounts && seasonNumber && episodeNumber
      ? `S${seasonNumber} E${episodeNumber}`
      : '';
  if (percentWatched && !watched) {
    text = seasonNumber && episodeNumber ? `Resume S${seasonNumber} E${episodeNumber}` : `Resume`;
  } else {
    if (isSeries(seriesType) && isLatest && !displaySeasonAndEpisodeCounts) text = `Play Latest`;
    if (isSeries(seriesType) && !isLatest) text = `Play ${seasonAndEpisodeNumberText}`;
  }
  return {
    label: text,
    url,
    type: button.WATCH,
    primary: true,
    shouldRender,
    onClick: () => {
      analytics?.handleSegmentSeriesItemClick(text);
    },
  };
};

const getRestartProps = ({ episode, analytics }) => {
  const shouldRender = !!(episode?.percentWatched && !episode?.watched);
  const text = 'Restart';
  const url = `${getPlayButtonUrl(episode)}mode/restart`;
  return {
    label: text,
    url,
    type: button.RESTART,
    shouldRender,
    onClick: () => {
      analytics?.handleSegmentSeriesItemClick(text);
    },
  };
};

const getUpsellProps = ({ analytics, activePlans }) => {
  const isFreeTrialPlanAvailable = activePlansHasFreeTrial(activePlans);
  const text = isFreeTrialPlanAvailable ? upsellPromoNavCTA : upsellPromoNavFreemiumCTA;
  return {
    label: text,
    primary: true,
    type: button.UPSELL,
    shouldRender: true,
    onClick: () => {
      analytics?.handleSegmentSeriesItemClick(text);
      redirectToExternalUrl(helperGetUpsellUrl());
    },
  };
};

const getTrailerProps = ({ item, hasTrailer, additionalFunctions, analytics }) => {
  const shouldRender = !!_.get(item, 'previewVideo.member.0', null);
  const text = hasTrailer ? 'Play Trailer' : 'Trailer';
  const clickHandler = () => {
    analytics?.handleSegmentSeriesItemClick(text);
    additionalFunctions?.setPlayerVisibility(true);
    additionalFunctions?.disableScroll({ disableScroll: true, bodyOnly: true });
  };

  return {
    label: text,
    url: null,
    type: hasTrailer ? button.WATCH : button.TRAILER,
    shouldRender,
    onClick: clickHandler,
  };
};

const getFavoriteProps = ({
  item = {},
  episode,
  favoritesManager: manager,
  favoritableItems,
  recommendationId,
  additionalFunctions,
}) => {
  const { id: showID, seriesType, network: networkId, name: seriesName } = item;
  const actionLocation = _.get(tealiumEventPageTypes, PageNames.DETAIL);
  const location = _.get(tealiumEventPageTypes, PageNames.FAVORITES_DETAIL);
  const showType = getShowType({ seriesType });
  const defaultText = 'Watchlist';
  const favoritedText = 'Added to Watchlist';
  const iconType = FAVORITE_PLUS;

  const data = {
    actionLocation,
    favoriteType: showType,
    favoritableItems,
    showID,
    source: SegmentSources.DETAIL,
    location,
    networkId,
    seriesName,
    recommendationId,
  };
  const favorites = _.get(manager, 'favorites');

  const favoriteButtonProps = {
    item,
    episode,
    disableScroll: additionalFunctions?.disableScroll,
    manager,
    favorites,
    data,
    defaultText,
    favoritedText,
    iconType,
    isHeroItem: true,
  };
  return favoriteButtonProps;
};

const getTrailerContext = ({ item, playerVisibility, additionalFunctions, isAnonymous }) => {
  const video = _.get(item, 'previewVideo.member.0', null);
  if (video) video.isTrailer = true;
  const title = _.get(video, 'name', '');
  const visibility = playerVisibility;
  const promoDisabled = !isAnonymous;

  const onClose = () => {
    additionalFunctions?.setPlayerVisibility(false);
    additionalFunctions?.disableScroll({ disableScroll: false, bodyOnly: false });
  };

  return {
    video,
    title,
    visibility,
    promoDisabled,
    onClose,
  };
};
const getSeriesButtons = ({
  item,
  episode,
  hasTrailer,
  analytics,
  additionalFunctions,
  isAnonymous = false,
}) => {
  const playButton = getPlayProps({ episode, item, isAnonymous, analytics });
  const restartButton = getRestartProps({ episode, analytics });
  const trailerButton = getTrailerProps({
    additionalFunctions,
    item,
    hasTrailer,
    analytics,
  });
  const buttons = [playButton, restartButton, trailerButton];
  return buttons.filter(button => button?.shouldRender);
};

const getV2SeriesButtons = ({
  activePlans,
  additionalFunctions,
  analytics,
  episode,
  hasTrailer,
  isAnonymous = false,
  item,
  showUpgradeCTA,
}) => {
  const UpsellButton = getUpsellProps({ analytics, activePlans });
  const playButton = getPlayProps({ episode, item, isAnonymous, analytics });
  const restartButton = getRestartProps({ episode, analytics });
  const trailerButton = getTrailerProps({
    additionalFunctions,
    item,
    hasTrailer,
    analytics,
  });
  const primaryButton = showUpgradeCTA ? UpsellButton : playButton;
  const buttons = [primaryButton, restartButton, trailerButton];
  return buttons.filter(button => button?.shouldRender);
};

const getPromoProps = ({ item, promos }) => {
  const promoLeadData = getPromoData({ data: promos, promoType: PROMO_TYPE_LEAD });
  const promoLeadContent = promoLeadData?.items?.member?.[0] ?? [];
  const promoDescription = promoLeadContent?.description ?? '';
  const promoCTA = promoLeadContent?.ctaText ?? '';
  const promoLink = promoLeadContent?.url ?? '';
  const onClickCTA = () => promoLink && redirectToExternalUrl(promoLink);

  return {
    onClickCTA,
    promoCTA,
    promoDescription,
    promoImage: item?.images?.seriesList?.raw ?? null,
    promoLogo: '/static/images/nation-logo-230x70.png',
  };
};

export {
  getAirDate,
  getAlternativeHeadline,
  getAvatarLogo,
  getBackgroundProps,
  getBadge,
  getButtons,
  getDescription,
  getEpisode,
  getEventDescription,
  getEventLogo,
  getEventStartTime,
  getFavoriteProps,
  getItemData,
  getLogo,
  getMetadata,
  getMobileInfoProps,
  getProgressBar,
  getPromoProps,
  getSeriesButtons,
  getV2SeriesButtons,
  getSeriesMetadata,
  getSeriesTuneIn,
  getShowcaseType,
  getTitle,
  getTrailerContext,
};
